import React, { useEffect, useRef, useState } from "react";
import { Sword, Trophy, Timer, ArrowsClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { fileUrl } from "../lib/api";
import { frameStyle } from "../lib/badgeFrames";
import { announce, AUTO_ANNOUNCE_KEY } from "../lib/announcer";
import { useAuth } from "../context/AuthContext";

const MEDALS = ["🥇", "🥈", "🥉"];

/**
 * GiftBattleOverlay — timed gifting contest during a live stream.
 *  - Host sees "Start Gift Battle" when idle, and "Start Rematch (2X)" when a
 *    loser has demanded a rematch.
 *  - Losers can demand a rematch at double stakes after a battle ends.
 *  - Everyone sees the countdown + standings (points = waves × multiplier).
 */
export default function GiftBattleOverlay({ sessionId, isHost = false }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [tick, setTick] = useState(0);
  const [celebrating, setCelebrating] = useState(null);
  const [demanded, setDemanded] = useState(false);
  const celebratedRef = useRef(new Set());

  useEffect(() => {
    if (!sessionId) return;
    let alive = true;
    const poll = async () => {
      try {
        const { data: d } = await api.get(`/live/${sessionId}/battle`);
        if (!alive) return;
        setData(d);
        const b = d?.battle;
        if (b?.ended && b.winner && !celebratedRef.current.has(b.id)) {
          celebratedRef.current.add(b.id);
          if (Date.now() - new Date(b.ends_at).getTime() < 30_000) {
            setCelebrating({ ...b.winner, multiplier: b.multiplier || 1 });
            setTimeout(() => setCelebrating(null), 6000);
          }
        }
      } catch (e) { /* silent */ }
    };
    poll();
    const t = setInterval(poll, 3000);
    const t2 = setInterval(() => setTick((x) => x + 1), 1000);
    return () => { alive = false; clearInterval(t); clearInterval(t2); };
  }, [sessionId]);

  const startBattle = async (rematch = false) => {
    try {
      await api.post(`/live/${sessionId}/battle/start`, { duration_sec: 180, rematch });
      setDemanded(false);
      toast.success(rematch ? "⚔️ REMATCH — stakes doubled!" : "⚔️ Gift battle started — 3 minutes!");
      if (isHost && localStorage.getItem(AUTO_ANNOUNCE_KEY) !== "0") {
        announce("battle_start", { duration: rematch ? "a three minute rematch at double stakes" : "three minutes" }, sessionId).catch(() => {});
      }
      const { data: d } = await api.get(`/live/${sessionId}/battle`);
      setData(d);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start battle");
    }
  };

  const demandRematch = async () => {
    try {
      const { data: r } = await api.post(`/live/${sessionId}/battle/rematch`);
      setDemanded(true);
      toast.success(`Rematch demanded — ${r.next_multiplier}X stakes if the host accepts!`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not demand rematch");
    }
  };

  const battle = data?.battle;
  const active = data?.active;
  const multiplier = battle?.multiplier || 1;
  let secondsLeft = 0;
  if (active && battle?.ends_at) {
    secondsLeft = Math.max(0, Math.floor((new Date(battle.ends_at).getTime() - Date.now()) / 1000));
  }
  const mm = String(Math.floor(secondsLeft / 60));
  const ss = String(secondsLeft % 60).padStart(2, "0");
  void tick;

  const iAmWinner = battle?.winner?.sender_id === user?.id;
  const rematchPending = !!battle?.rematch;

  return (
    <>
      {celebrating && (
        <div className="fixed inset-0 z-[90] flex items-center justify-center pointer-events-none" data-testid="battle-winner-overlay">
          <div className="crystal-glass rounded-3xl px-8 py-6 text-center animate-fade-up">
            <div className="text-6xl animate-bounce">🏆</div>
            <p className="text-jam-gold text-[10px] tracking-[0.35em] uppercase font-black mt-2">
              {celebrating.multiplier > 1 ? `${celebrating.multiplier}X Rematch Champion` : "Battle Champion"}
            </p>
            <p className="text-cream text-3xl font-black tracking-tighter mt-1">@{celebrating.sender_username}</p>
            <p className="text-papaya text-sm font-bold mt-1">{celebrating.total_waves} waves · {celebrating.gift_count} gifts</p>
          </div>
        </div>
      )}

      {active && battle ? (
        <div className="rounded-2xl crystal-glass px-3 py-2 border border-jam-gold/40" data-testid="battle-panel">
          <div className="flex items-center justify-between">
            <span className="flex items-center gap-1.5 text-jam-gold text-[9px] tracking-[0.3em] uppercase font-black">
              <Sword size={12} weight="fill" /> Gift Battle
              {multiplier > 1 && (
                <span className="rounded-full bg-red-500 text-white px-1.5 py-0.5 text-[8px] animate-pulse" data-testid="battle-multiplier">
                  {multiplier}X STAKES
                </span>
              )}
            </span>
            <span className="flex items-center gap-1 text-cream text-sm font-black tabular-nums" data-testid="battle-countdown">
              <Timer size={13} className="text-jam-gold" weight="fill" /> {mm}:{ss}
            </span>
          </div>
          <div className="flex items-center gap-1.5 mt-1.5 overflow-x-auto">
            {(data.leaderboard || []).length === 0 ? (
              <span className="text-cream/50 text-[10px]">Send a gift to take the lead!</span>
            ) : (
              data.leaderboard.map((r, i) => {
                const fs = frameStyle(r.sender_badge_frame);
                return (
                <div key={r.sender_id} className={`flex items-center gap-1 rounded-full px-2 py-0.5 flex-shrink-0 border ${fs ? `${fs.bg} ${fs.border}` : "bg-white/10 border-transparent"}`} data-testid={`battle-leader-${i + 1}`} data-badge-frame={r.sender_badge_frame || ""}>
                  <span className="text-[10px]">{MEDALS[i] || "🏅"}</span>
                  {r.sender_avatar_path && (
                    <img src={fileUrl(r.sender_avatar_path)} alt="" className={`w-3.5 h-3.5 rounded-full object-cover ${fs ? fs.ring : ""}`} />
                  )}
                  <span className="text-cream text-[9px] font-bold truncate max-w-[64px]">@{r.sender_username}</span>
                  {fs && <span className="text-[9px] leading-none">{r.sender_badge_emoji}</span>}
                  <span className="text-jam-gold text-[9px] font-black">{r.points ?? r.total_waves} pts</span>
                </div>
                );
              })
            )}
          </div>
        </div>
      ) : isHost ? (
        <div className="flex items-center gap-2 pointer-events-auto">
          {rematchPending ? (
            <button
              onClick={() => startBattle(true)}
              data-testid="battle-rematch-start-btn"
              className="inline-flex items-center gap-1.5 rounded-full bg-red-500 text-white px-4 py-2 text-[10px] font-black tracking-[0.2em] uppercase hover:brightness-110 animate-pulse"
            >
              <ArrowsClockwise size={13} weight="bold" />
              @{battle.rematch.by_username} demands a rematch — Start {Math.min(multiplier * 2, 8)}X
            </button>
          ) : (
            <button
              onClick={() => startBattle(false)}
              data-testid="battle-start-btn"
              className="inline-flex items-center gap-1.5 rounded-full bg-jam-gold text-jam-black px-4 py-2 text-[10px] font-black tracking-[0.2em] uppercase hover:brightness-110 transition-all"
            >
              <Sword size={13} weight="fill" /> Start Gift Battle
            </button>
          )}
        </div>
      ) : battle?.ended && battle.winner ? (
        <div className="flex items-center gap-2 flex-wrap">
          <div className="inline-flex items-center gap-1.5 rounded-full crystal-glass px-3 py-1.5" data-testid="battle-last-winner">
            <Trophy size={12} className="text-jam-gold" weight="fill" />
            <span className="text-cream/70 text-[10px] font-bold">Champion: @{battle.winner.sender_username} ({battle.winner.total_waves}W)</span>
          </div>
          {!iAmWinner && !rematchPending && !demanded && (
            <button
              onClick={demandRematch}
              data-testid="battle-demand-rematch-btn"
              className="inline-flex items-center gap-1 rounded-full bg-red-500/20 border border-red-500/50 text-red-300 px-3 py-1.5 text-[10px] font-black tracking-[0.15em] uppercase hover:bg-red-500/30"
            >
              <ArrowsClockwise size={12} weight="bold" /> Demand Rematch · {Math.min(multiplier * 2, 8)}X
            </button>
          )}
          {(rematchPending || demanded) && (
            <span className="text-red-300 text-[10px] font-bold animate-pulse" data-testid="battle-rematch-pending">
              ⚔️ Rematch demanded — waiting on host…
            </span>
          )}
        </div>
      ) : null}
    </>
  );
}
